import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff, ArrowLeft, Loader2, Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage, LangSwitcher } from "@/context/LanguageContext";
import { toast } from "sonner";
import { tr } from "@/lib/i18n";

const BG = "https://images.unsplash.com/photo-1776218643389-98159ee68366?q=80&w=1920&auto=format&fit=crop";

export default function DeveloperLogin() {
    const { user, login, logout } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    useLanguage();

    if (user?.role === "developer") return <Navigate to="/developer" replace />;

    const submit = async (e) => {
        e?.preventDefault();
        if (!email || !password || busy) return;
        setBusy(true);
        try {
            const u = await login(email, password);
            if (u.role === "developer") {
                toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
                navigate("/developer");
            } else {
                toast.error(tr("That's a staff account. Please use the staff sign-in."));
                await logout();
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Sign in failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen relative flex items-center justify-center overflow-hidden" data-testid="developer-login-page">
            <div className="absolute inset-0 bg-cover bg-center scale-105" style={{ backgroundImage: `url(${BG})`, filter: "blur(6px) brightness(0.55)" }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, rgba(10,37,64,0.8) 0%, rgba(10,37,64,0.45) 55%, rgba(184,134,11,0.25) 100%)" }} />

            <Link to="/" data-testid="devlogin-back" className="absolute top-6 left-6 z-10 inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors">
                <ArrowLeft className="w-4 h-4" /> {tr("Back to home")}
            </Link>
            <div className="absolute top-6 right-6 z-10"><LangSwitcher dark testid="devlogin-lang-switcher" /></div>

            <motion.div initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.5, ease: "easeOut" }}
                className="relative z-10 w-full max-w-md mx-4">
                <div className="rounded-[20px] p-8 sm:p-10 shadow-2xl border border-white/15" style={{ background: "rgba(13,33,54,0.72)", backdropFilter: "blur(28px)" }}>
                    <div className="flex flex-col items-center text-center">
                        <img src="/assets/logo-light.png" alt="LandLogic NexusOne" className="w-24 h-auto drop-shadow-2xl" />
                        <div className="mt-3 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#00B4D8]/15 border border-[#00B4D8]/30 text-[11px] font-bold text-[#7CD4EA]">
                            <Building2 className="w-3.5 h-3.5" /> {tr("APPLICANT PORTAL")}
                        </div>
                        <p className="text-sm text-white/60 mt-3">{tr("City of Brampton · Applicant access")}</p>
                    </div>

                    <div className="mt-6">
                        <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-white/50 mb-3 text-center">{tr("Applicant demo account")}</div>
                        <button data-testid="devlogin-demo-mike" onClick={() => { setEmail("mike@devcorp.ca"); setPassword("Developer2026!"); }}
                            className="w-full flex items-center gap-3.5 p-3.5 rounded-[14px] border border-white/10 bg-white/5 hover:bg-white/10 hover:border-[#FFB703]/50 transition-all group">
                            <div className="w-12 h-12 rounded-full bg-[#B8860B] flex items-center justify-center text-white font-heading font-bold text-sm ring-2 ring-white/20 group-hover:ring-[#FFB703]/60 transition-all">MD</div>
                            <div className="text-left">
                                <div className="text-sm font-bold text-white">Mike Delacroix</div>
                                <div className="text-[11px] text-white/50">DevCorp Communities · {tr("2 active files")}</div>
                            </div>
                        </button>
                    </div>

                    <form onSubmit={submit} className="mt-6 space-y-3.5">
                        <input data-testid="devlogin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                            placeholder={tr("Applicant account email")} autoComplete="email"
                            className="w-full rounded-[10px] bg-white/10 border border-white/15 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent transition-all" />
                        <div className="relative">
                            <input data-testid="devlogin-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                                placeholder={tr("Password")} autoComplete="current-password"
                                className="w-full rounded-[10px] bg-white/10 border border-white/15 px-4 py-3 pr-11 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent transition-all" />
                            <button type="button" data-testid="devlogin-toggle-pw" onClick={() => setShowPw(!showPw)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors">
                                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                        <button type="submit" data-testid="devlogin-submit" disabled={busy || !email || !password}
                            className="w-full py-3.5 rounded-[10px] bg-[#00B4D8] hover:bg-[#00c3eb] text-white font-bold text-sm transition-all hover:shadow-lg hover:shadow-[#00B4D8]/30 active:scale-[0.98] disabled:opacity-40 inline-flex items-center justify-center gap-2">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                            {tr("Sign in to applicant portal")}
                        </button>
                    </form>

                    <div className="mt-6 flex items-center justify-between text-xs">
                        <Link to="/register" data-testid="devlogin-register" className="text-[#7CD4EA] font-bold hover:underline">{tr("Register as an applicant")}</Link>
                        <Link to="/login" data-testid="devlogin-staff-link" className="text-white/50 hover:text-white transition-colors">{tr("Staff sign in →")}</Link>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
